import { Grid, Icon, Modal } from 'semantic-ui-react';
import { TariffDto, TariffSearchRequest, UpsertTariffDto } from 'ts-types/api.types';
import axios, { CancelTokenSource } from 'axios';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import Input from 'components/final-form/Input';
import { deleteTariffType, findTariffTypeById, searchTariffTypes } from 'services/tariffServices';
import { debounce } from 'ts-debounce';
import { FormState } from 'final-form';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import UpsertTariffTypeForm from 'components/tariffs/UpsertTariffTypeForm';
import { noop } from 'utils/functionUtils';
import DeleteActionButton from 'components/DeleteActionButton';
import LoaderComponent from 'components/LoaderComponent';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PxGrid from 'components/layout/PxGrid';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import EditActionButton from 'components/EditActionButton';
import AddButton from 'components/AddButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import SearchResultComponent from 'components/SearchResultComponent';

const initialTariffTypeSearch: Partial<TariffSearchRequest> = {};

interface TariffTypeFieldProps extends FormState {
  values: Partial<TariffSearchRequest>
}

interface Props extends MessageSourceProps {
}

interface State {
  foundTariffTypes: Array<TariffDto>,
  tariffTypesLoaded: boolean,
  tariff?: UpsertTariffDto,
  tariffId?: number,
  searchValues: Partial<TariffSearchRequest>,
  addEditModalOpen: boolean,
  cancelTokenSource: CancelTokenSource,
}

class TariffTypesView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundTariffTypes: [],
      tariffTypesLoaded: false,
      tariff: undefined,
      tariffId: undefined,
      searchValues: initialTariffTypeSearch,
      addEditModalOpen: false,
      cancelTokenSource
    };
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  openAddEditModal = (id: number | undefined) => {

    const {cancelTokenSource} = this.state;

    if (id) {
      findTariffTypeById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop);
    }
  };

  showModal = (id?: number, tariff?: UpsertTariffDto) => {

    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        tariff: tariff,
        tariffId: id
      }
    });
  };

  hideModal = () => {

    this.setState(({}: State) => ({
      addEditModalOpen: false,
      tariffId: undefined,
      tariff: undefined
    }));
  };

  onSaveUpsertForm = () => {
    const {searchValues, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        tariffTypesLoaded: true
      })
    };

    searchTariffTypes(searchValues, cancelTokenSource)
        .then(response => {
          this.setState({
            foundTariffTypes: response
          });
        })
        .catch(noop)
        .finally(onFinally);
    this.hideModal();
  };

  deleteTariffType = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteTariffType(id, cancelTokenSource).then(response => {
      this.setState(({foundTariffTypes}: State) => ({
        foundTariffTypes: foundTariffTypes.filter((tariff: TariffDto) => tariff.id != response.id)
      }))
    }).catch(noop)
  };

  handleTariffTypeChange = debounce(({values}: TariffTypeFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    const onFinally = () => {
      this.setState({
        tariffTypesLoaded: true
      })
    };

    const {cancelTokenSource} = this.state;
    this.setState({tariffTypesLoaded: false});
    searchTariffTypes(values, cancelTokenSource)
        .then(response => {
          this.setState({
            foundTariffTypes: response,
            searchValues: values
          });
        })
        .catch(noop)
        .finally(onFinally);
  }, 300);

  render(): React.ReactNode {
    const {tariffTypesLoaded} = this.state;
    const {getMessage} = this.props;

    return (
        <PraxStyledSegment>
          <div>
            {this.renderSearchTariffTypesSegment()}
          </div>
          <StyledViewTableRow>
            {!tariffTypesLoaded
                ? <LoaderComponent message={getMessage("tariff.type.loading")}/>
                : this.renderTariffTypesSegment()}
          </StyledViewTableRow>
          {this.renderUpsertTariffTypeModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchTariffTypesSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{}}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {foundTariffTypes} = this.state;

    const numResults = foundTariffTypes.length;

    return (
        <PxGrid>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("description")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="description"
                  fluid
                  component={Input}
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleTariffTypeChange}/>
        </PxGrid>
    );
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("tariff.type.confirmDelete")}
              onConfirm={() => this.deleteTariffType(cellData)}
          />
        </React.Fragment>
    );
  };

  tariffTypesRowGetter = ({index}: any) => {
    const {foundTariffTypes} = this.state;

    Object.assign(foundTariffTypes[index], {index: index + 1});

    return foundTariffTypes[index]
  };

  foundTariffTypesRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundTariffTypes} = this.state;
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(foundTariffTypes[index].id)}
        >
          {columns}
        </div>
    );
  };

  notificationCellRenderer = ({cellData}: any) => {
    if (cellData) {
      return <Icon name="check" size="small"/>
    }
    return ""
  }

  tarmedTypeCellRenderer = ({cellData}: any) => {

    const {getMessage} = this.props;

    if (cellData) {

      return getMessage(`tt.${cellData.toLowerCase()}`)
    }
  }

  renderTariffTypesSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundTariffTypes} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundTariffTypes.length}
            rowGetter={this.tariffTypesRowGetter}
            rowRenderer={this.foundTariffTypesRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("description")),
                dataKey: "description"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("tariff.connection")),
                dataKey: "lnkTrfIdDescription"
              },
              {
                width: 300,
                label: (getMessage("tariff.tarmed.type")),
                dataKey: "type",
                cellRenderer: (this.tarmedTypeCellRenderer)
              },
              {
                width: 200,
                label: getMessage("tariff.basic"),
                dataKey: "baseTariff",
                cellRenderer: this.notificationCellRenderer
              },
              {
                width: 200,
                label: getMessage("tariff.tarmed"),
                dataKey: "tarmedTariff",
                cellRenderer: this.notificationCellRenderer
              },
              {
                width: 200,
                label: getMessage("tariff.active"),
                dataKey: "active",
                cellRenderer: this.notificationCellRenderer
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  renderUpsertTariffTypeModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, tariff, tariffId} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("tariff.type.header")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertTariffTypeForm
                tariff={tariff}
                tariffId={tariffId}
                onCancel={this.hideModal}
                onSave={this.onSaveUpsertForm}
            />
          </FormModalContentContainer>
        </Modal>
    );
  }

}

export default withMessages(TariffTypesView);
